import { useState, useEffect } from 'react';
import { getApp } from 'firebase/app';
import {
  getFirestore, collection, query, where, orderBy,
  onSnapshot, limit,
} from 'firebase/firestore';
import AccessRequestsTab from '../components/AdminPanel/AccessRequestsTab';
import InviteLinksTab    from '../components/AdminPanel/tabs/InviteLinksTab';

const DB = () => { try { return getFirestore(getApp()); } catch (_) { return null; } };

const STATUS_CONFIG = {
  pending:        { label: 'PENDING',        color: '#f59e0b', bg: 'rgba(245,158,11,0.12)'  },
  approved:       { label: 'APPROVED',       color: '#10b981', bg: 'rgba(16,185,129,0.12)'  },
  rejected:       { label: 'REJECTED',       color: '#ef4444', bg: 'rgba(239,68,68,0.12)'   },
  denied:         { label: 'REJECTED',       color: '#ef4444', bg: 'rgba(239,68,68,0.12)'   },
  info_requested: { label: 'INFO REQUESTED', color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)'  },
  info_responded: { label: 'INFO RECEIVED',  color: '#0e9cc0', bg: 'rgba(14,156,192,0.12)'  },
};

const fmt = ts => ts?.toDate?.()?.toLocaleString('en-IN', {
  day: 'numeric', month: 'short', year: 'numeric',
  hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
}) || '—';

// ── My request status (non-admin) ─────────────────────────────────────────────
function MyRequestStatus({ userId }) {
  const [req,     setReq]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const db = DB();
    if (!db || !userId) { setLoading(false); return; }
    const q = query(
      collection(db, 'access_requests'),
      where('uid', '==', userId),
      orderBy('submittedAt', 'desc'),
      limit(1),
    );
    const unsub = onSnapshot(q, snap => {
      setReq(snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() });
      setLoading(false);
    }, () => {
      const q2 = query(
        collection(db, 'access_requests'),
        where('uid', '==', userId),
        orderBy('requestedAt', 'desc'),
        limit(1),
      );
      onSnapshot(q2, snap => {
        setReq(snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() });
        setLoading(false);
      }, () => setLoading(false));
    });
    return unsub;
  }, [userId]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60, fontFamily: 'monospace', fontSize: 11, color: '#8892a4' }}>
        LOADING REQUEST STATUS…
      </div>
    );
  }

  if (!req) {
    return (
      <div style={{ maxWidth: 520, margin: '60px auto', padding: '0 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>📋</div>
        <div style={{ fontFamily: 'monospace', fontSize: 13, color: '#d9e2ff', marginBottom: 8 }}>
          NO ACCESS REQUEST FOUND
        </div>
        <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#8892a4' }}>
          You have not submitted an access request.
        </div>
      </div>
    );
  }

  const st = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;

  return (
    <div style={{ maxWidth: 640, margin: '40px auto', padding: '0 24px' }}>
      <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: 9, color: '#65d4fa',
        letterSpacing: '0.2em', marginBottom: 20, textTransform: 'uppercase' }}>
        ACCESS REQUEST STATUS
      </div>

      <div style={{ background: 'var(--smc-panel, #0d1526)', border: '1px solid var(--smc-border, #1e2d45)',
        borderRadius: 8, overflow: 'hidden' }}>

        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--smc-border, #1e2d45)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#8892a4',
              letterSpacing: '0.1em', marginBottom: 4 }}>SUBMITTED</div>
            <div style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--smc-tx, #d9e2ff)' }}>
              {fmt(req.submittedAt || req.requestedAt)}
            </div>
          </div>
          <div>
            <div style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 4,
              background: st.bg, border: `1px solid ${st.color}44`,
              fontFamily: "'Share Tech Mono', monospace", fontSize: 10,
              color: st.color, letterSpacing: '0.12em' }}>
              {st.label}
            </div>
          </div>
        </div>

        <div style={{ padding: '20px 24px' }}>
          <Row label="NAME"           value={req.name || '—'} />
          <Row label="EMAIL"          value={req.email || '—'} />
          <Row label="REQUESTED ROLE" value={req.roleLabel || req.role || '—'} />
          {req.reason && <Row label="REASON" value={req.reason} />}
          {req.reviewedBy && <Row label="REVIEWED BY" value={req.reviewedBy} accent="#65d4fa" />}
          {req.reviewedAt && <Row label="REVIEWED AT" value={fmt(req.reviewedAt)} />}
          {req.reviewNote && <Row label="ADMIN NOTE" value={req.reviewNote} accent="#f59e0b" />}
          {req.approvedRole && req.status === 'approved' && (
            <Row label="APPROVED AS" value={req.approvedRole.toUpperCase()} accent="#10b981" />
          )}
          {req.infoResponse && <Row label="YOUR RESPONSE" value={req.infoResponse} />}
        </div>

        {req.status === 'pending' && (
          <div style={{ padding: '14px 24px', borderTop: '1px solid var(--smc-border, #1e2d45)',
            background: 'rgba(245,158,11,0.05)', fontFamily: 'monospace', fontSize: 11,
            color: '#f59e0b', letterSpacing: '0.08em' }}>
            ⏳ Your request is awaiting review by the admin team.
          </div>
        )}
        {req.status === 'approved' && (
          <div style={{ padding: '14px 24px', borderTop: '1px solid var(--smc-border, #1e2d45)',
            background: 'rgba(16,185,129,0.05)', fontFamily: 'monospace', fontSize: 11,
            color: '#10b981', letterSpacing: '0.08em' }}>
            ✅ Your request has been approved. You can now sign in with your credentials.
          </div>
        )}
        {(req.status === 'rejected' || req.status === 'denied') && (
          <div style={{ padding: '14px 24px', borderTop: '1px solid var(--smc-border, #1e2d45)',
            background: 'rgba(239,68,68,0.05)', fontFamily: 'monospace', fontSize: 11,
            color: '#ef4444', letterSpacing: '0.08em' }}>
            ❌ Your request was not approved. Contact the admin team for more information.
          </div>
        )}
        {req.status === 'info_requested' && (
          <div style={{ padding: '14px 24px', borderTop: '1px solid var(--smc-border, #1e2d45)',
            background: 'rgba(139,92,246,0.05)', fontFamily: 'monospace', fontSize: 11,
            color: '#8b5cf6', letterSpacing: '0.08em' }}>
            💬 The admin team has requested more information. Check your email for details.
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div style={{ display: 'flex', gap: 16, marginBottom: 12, alignItems: 'flex-start' }}>
      <div style={{ fontFamily: 'monospace', fontSize: 10, color: '#8892a4',
        letterSpacing: '0.1em', minWidth: 120, paddingTop: 1, flexShrink: 0 }}>
        {label}
      </div>
      <div style={{ fontFamily: 'monospace', fontSize: 12,
        color: accent || 'var(--smc-tx, #d9e2ff)', flex: 1, wordBreak: 'break-word' }}>
        {value}
      </div>
    </div>
  );
}

// ── Admin view with two sub-tabs ──────────────────────────────────────────────
function AdminRequestsView() {
  const [tab, setTab] = useState('signup');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 500 }}>

      {/* Sub-tab strip */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #1e2636',
        background: '#070c1a', flexShrink: 0 }}>
        {[
          { id: 'signup',  label: '👤  SIGN-UP REQUESTS', color: '#f59e0b' },
          { id: 'invites', label: '🔗  GUEST INVITES',    color: '#65d4fa' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding: '11px 22px', fontFamily: 'monospace', fontSize: 9,
              letterSpacing: 1, cursor: 'pointer', background: 'transparent',
              border: 'none', whiteSpace: 'nowrap',
              borderBottom: `2px solid ${tab === t.id ? t.color : 'transparent'}`,
              color: tab === t.id ? t.color : '#445566',
              transition: 'color 0.15s' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {tab === 'signup'  && <AccessRequestsTab />}
        {tab === 'invites' && <GuestInvitesPanel />}
      </div>
    </div>
  );
}

// ── Guest invites panel — wraps InviteLinksTab with context copy ──────────────
function GuestInvitesPanel() {
  return (
    <div style={{ height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

      {/* Context banner */}
      <div style={{ padding: '14px 22px', background: 'rgba(101,212,250,0.05)',
        borderBottom: '1px solid rgba(101,212,250,0.12)', flexShrink: 0 }}>
        <div style={{ fontFamily: 'monospace', fontSize: 10, color: '#65d4fa',
          letterSpacing: 1, marginBottom: 4 }}>GUEST ACCESS INVITES</div>
        <div style={{ fontFamily: 'monospace', fontSize: 10, color: '#445566', lineHeight: 1.6 }}>
          Create invite links for <span style={{ color: '#a855f7' }}>Mentors</span>,{' '}
          <span style={{ color: '#f59e0b' }}>Investors</span>, or{' '}
          <span style={{ color: '#06b6d4' }}>Guest</span> reviewers.
          They log in and see approved reports with read + comment access.
        </div>
      </div>

      {/* InviteLinksTab — renders itself with overflow */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <InviteLinksTab guestOnly />
      </div>
    </div>
  );
}

// ── Public export ─────────────────────────────────────────────────────────────
export default function AccessRequestsPage({ userId, role }) {
  if (role === 'admin' || role === 'ops') {
    return (
      <div style={{ padding: '0' }}>
        <AdminRequestsView />
      </div>
    );
  }

  return <MyRequestStatus userId={userId} />;
}
